import {
  useCartLineItems,
  useCartShippingPrice,
  useCartTaxPrice,
  useCartTotalAmount
} from '@ecomm/data-cart'
import { useLocale } from '@ecomm/data-hooks'
import { useMicroCopy } from '@ecomm/micro-copy'
import {
  getDiscountedAmount,
  getPricesBeforeDiscount
} from '@simplisafe/eis-commercetools-carts'
import { formatPrice } from '@simplisafe/eis-commercetools-shared'

/**
 * Renders the subtotal, coupon code applied, shipping, estimated tax, and total.
 * Appears below the accordion component within the OrderSummary component.
 */
export function Totals() {
  const microCopy = useMicroCopy()
  const locale = useLocale()
  const lineItems = useCartLineItems()
  const total_ = useCartTotalAmount()
  const shippingPrice_ = useCartShippingPrice()
  const taxPrice_ = useCartTaxPrice()

  const priceBeforeDiscount = getPricesBeforeDiscount(lineItems, locale)
  const subtotal = formatPrice(priceBeforeDiscount, locale)

  // manually exclude shipping cost until ECP-13311 is completed
  const discountedAmount = getDiscountedAmount(
    lineItems,
    total_ - shippingPrice_,
    locale
  )
  const couponCodeApplied = formatPrice(discountedAmount, locale)

  const shipping = formatPrice(shippingPrice_, locale)

  const estimatedTax = formatPrice(taxPrice_, locale)

  // VAT is built in to pricing, otherwise add tax to display
  const totalCents = locale === 'en-GB' ? total_ : total_ + taxPrice_
  const total = formatPrice(totalCents, locale)

  return (
    <div className="flex flex-col">
      <hr
        className="border-neutral-medium-100 my-4 w-full border border-b-0 border-solid"
        data-component="Divider"
      />
      <div className="my-2 mb-2 flex w-full justify-between md:my-1.5">
        <div className="text-base md:text-lg">Subtotal:</div>
        <div className="font-regular whitespace-nowrap pl-2 text-base md:text-lg">
          {subtotal}
        </div>
      </div>
      {discountedAmount ? (
        <div className="my-2 mb-2 flex w-full justify-between md:my-1.5">
          <div className="text-base md:text-lg">Coupon code applied:</div>
          <div className="font-regular text-sale whitespace-nowrap pl-2 text-base md:text-lg">
            {couponCodeApplied}
          </div>
        </div>
      ) : null}
      <div className="my-2 mb-2 flex w-full justify-between md:my-1.5">
        <div className="text-base md:text-lg">Shipping:</div>
        <div className="font-regular whitespace-nowrap pl-2 text-base md:text-lg">
          {shipping}
        </div>
      </div>
      <div className="my-2 mb-2 flex w-full justify-between md:my-1.5">
        <div className="text-base md:text-lg">{microCopy['checkout-tax']}</div>
        <div className="font-regular whitespace-nowrap pl-2 text-base md:text-lg">
          {estimatedTax}
        </div>
      </div>
      <hr
        className="border-neutral-medium-100 my-4 w-full border border-b-0 border-solid"
        data-component="Divider"
      />
      <div className="my-2 mb-2 flex w-full justify-between md:my-1.5">
        <div className="text-base font-bold md:text-lg">
          {microCopy['checkout-total']}
        </div>
        <div className="font-regular whitespace-nowrap pl-2 text-base font-bold md:text-lg">
          {total}
        </div>
      </div>
    </div>
  )
}
